import { Select, Row, Col, Card, Dropdown, Menu, Divider } from 'antd';
import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';

import GlossaryRelatedTermsResult, { RelatedTermTypes } from './GlossaryRelatedTermsResult';
import { ReactComponent as MoreAction } from '../../../../images/more-vert.svg';
import AddRelatedTermsModal from './AddRelatedTermsModal';

import { PaginationBar } from '../../shared/tabs/Glossary/PaginationBar';
import { usePagination } from '../../../shared/usePagination';
import { useEntityData } from '../../shared/EntityContext';
import { EntityType } from '../../../../types.generated';

const NONE_OPTION = 'none';

const ActionButton = styled.div`
    display: flex;
    cursor: pointer;
    align-items: center;
`;

export const BrowseMenu = styled(Menu)`
    padding: 0px;
    border-radius: 4px;
    min-width: 140px !important;
`;

const styles = {
    title: { margin: 0 },
    row: { padding: '12px 16px 12px 24px' },
};

export default function GlossayRelatedTerms() {
    const menuOptionsArray = Object.keys(RelatedTermTypes);
    const { entityData, loading } = useEntityData();

    const [isTypeTermGroup, setIsTypeTermGroup] = useState(false);
    const [selectedKey, setSelectedKey] = useState(NONE_OPTION);
    const [showAddModal, setShowAddModal] = useState(false);
    const [isAllLoaded, setIsAllLoaded] = useState(false);

    const termsLoadingRef = useRef<Record<string, boolean>>({});

    const mapRelationshipType = (option: string) => {
        const relationships = entityData?.[option]?.relationships || entityData?.[option]?.nodes || [];

        return relationships.map((relationship) => ({
            ...relationship,
            relationshipType: option,
        }));
    };

    // Combine all related terms data when "None" option is selected
    const allRelatedTermsResult = () => {
        if (selectedKey === NONE_OPTION && entityData) {
            // Combine all relationships/nodes from all types
            const allRelationships: any[] = [];
            menuOptionsArray.forEach((option) => {
                const relationships = mapRelationshipType(option);

                allRelationships.push(...relationships);
            });
            return allRelationships;
        }

        return selectedKey && entityData ? mapRelationshipType(selectedKey) : [];
    };

    const { pageSize, page, totalCount, getItemsForPage, handleChangePage, handleShowSizeChange, resetPagination } =
        usePagination({
            items: allRelatedTermsResult() || [],
        });

    // Handle the dropdown change
    const handleSelectChange = (value) => {
        setSelectedKey(value);
        resetPagination();
    };

    // Open the add term modal
    const handleAddTerm = () => {
        setIsTypeTermGroup(false);
        setShowAddModal(true);
    };

    // Open the add term group modal
    const handleAddTermGroup = () => {
        setIsTypeTermGroup(true);
        setShowAddModal(true);
    };

    const trackTermLoading = (urn: string, trackLoading: boolean) => {
        termsLoadingRef.current[urn] = trackLoading;
        const allTermsLoaded = Object.values(termsLoadingRef.current).every((isLoading) => !isLoading);
        setIsAllLoaded(allTermsLoaded);
    };

    // Show "All Relations" when "None" option is selected
    const displayType = selectedKey === NONE_OPTION ? 'All Relations' : RelatedTermTypes[selectedKey];

    // Show the dropdown menu
    const browseMenu = (
        <BrowseMenu>
            <Menu.Item key="add-term" className="f-color-gray-s100" onClick={handleAddTerm}>
                Add Glossary Term
            </Menu.Item>
            <Divider className="ma-0" />
            <Menu.Item key="add-group" className="f-color-gray-s100" onClick={handleAddTermGroup}>
                Add Glossary Term Group
            </Menu.Item>
        </BrowseMenu>
    );

    return (
        <Card>
            {/* Header */}
            <Row style={styles.row} justify="space-between" align="middle">
                <Col lg={4} md={6} sm={6} xs={24}>
                    <Select
                        value={selectedKey}
                        style={{ width: '100%' }}
                        onChange={handleSelectChange}
                        placeholder="Select relation type"
                        data-testid="related-terms-dropdown"
                    >
                        <Select.Option data-testid="none" key={NONE_OPTION} value={NONE_OPTION}>
                            All Relations
                        </Select.Option>
                        {menuOptionsArray.map((option) => (
                            <Select.Option data-testid={option} key={option} value={option}>
                                {RelatedTermTypes[option]}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col flex="none">
                    <Dropdown overlay={browseMenu} trigger={['click']}>
                        <ActionButton>
                            <MoreAction
                                width={12}
                                height={12}
                                className="f-color-gray-s100"
                                data-testid="entity-header-dropdown"
                            />
                            <span className="f-color-gray-s100 pl-1">Actions</span>
                        </ActionButton>
                    </Dropdown>
                </Col>
            </Row>
            <Divider className="ma-0" />

            {/* Related Terms Result */}
            <Row>
                <Col xs={24}>
                    {selectedKey && entityData && (
                        <GlossaryRelatedTermsResult
                            loading={!isAllLoaded || loading}
                            trackTermLoading={trackTermLoading}
                            glossaryRelatedTermType={displayType}
                            glossaryRelatedTermResult={getItemsForPage(page)}
                        />
                    )}
                </Col>
            </Row>

            {/* Pagination */}
            {totalCount > 0 && (
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    loading={!isAllLoaded || loading}
                    totalCount={totalCount}
                    onChangePage={handleChangePage}
                    onShowSizeChange={handleShowSizeChange}
                />
            )}

            {/* Add Term Modal */}
            {showAddModal && (
                <AddRelatedTermsModal
                    isTypeTermGroup={isTypeTermGroup}
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setShowAddModal(false)}
                />
            )}
        </Card>
    );
}
